/*
  Отчётные снимки программы — только добавление (append-only).
  Цепочка хешей связывает снимки; удаление из UI не предусмотрено.
*/

import type { GanttItem } from '@/data/projects';
import type { EditableProject } from '@/store/projectStore';

export const REVIEW_SNAPSHOTS_KEY = 'diak_review_snapshots_v1';
export const SNAPSHOT_SCHEMA_VERSION = 1 as const;

export interface ReviewSnapshotPayload {
  schemaVersion: typeof SNAPSHOT_SCHEMA_VERSION;
  projects: EditableProject[];
  ganttItems: GanttItem[];
}

export interface ReviewSnapshot {
  id: string;
  createdAt: string;
  authorNote: string;
  payload: ReviewSnapshotPayload;
  /** Хеш содержимого предыдущего снимка в цепочке (null для первого). */
  previousSnapshotHash: string | null;
  /** SHA-256 от канонического JSON записи без этого поля. */
  contentHash: string;
}

function canonicalStringify(value: unknown): string {
  if (value === null) return 'null';
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((v) => canonicalStringify(v)).join(',')}]`;
  }
  if (t === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalStringify(obj[k])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

async function sha256Hex(text: string): Promise<string> {
  const enc = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function clonePayload(projects: EditableProject[], ganttItems: GanttItem[]): ReviewSnapshotPayload {
  return {
    schemaVersion: SNAPSHOT_SCHEMA_VERSION,
    projects: JSON.parse(JSON.stringify(projects)) as EditableProject[],
    ganttItems: JSON.parse(JSON.stringify(ganttItems)) as GanttItem[],
  };
}

function buildRecordForHash(
  id: string,
  createdAt: string,
  authorNote: string,
  payload: ReviewSnapshotPayload,
  previousSnapshotHash: string | null,
): string {
  return canonicalStringify({ id, createdAt, authorNote, payload, previousSnapshotHash });
}

export function loadReviewSnapshots(): ReviewSnapshot[] {
  try {
    const raw = localStorage.getItem(REVIEW_SNAPSHOTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ReviewSnapshot[];
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveReviewSnapshots(snapshots: ReviewSnapshot[]): void {
  localStorage.setItem(REVIEW_SNAPSHOTS_KEY, JSON.stringify(snapshots));
}

/** Собрать запись снимка (без записи в storage). previousHash — хеш последнего снимка в цепочке. */
export async function buildReviewSnapshotRecord(
  projects: EditableProject[],
  ganttItems: GanttItem[],
  authorNote: string,
  previousSnapshotHash: string | null,
): Promise<ReviewSnapshot> {
  const id = `snap-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const createdAt = new Date().toISOString();
  const payload = clonePayload(projects, ganttItems);
  const contentHash = await sha256Hex(
    buildRecordForHash(id, createdAt, authorNote.trim(), payload, previousSnapshotHash),
  );
  return {
    id,
    createdAt,
    authorNote: authorNote.trim(),
    payload,
    previousSnapshotHash,
    contentHash,
  };
}

/**
 * Добавить снимок текущего состояния. Не изменяет и не удаляет предыдущие записи.
 */
export async function appendReviewSnapshot(
  projects: EditableProject[],
  ganttItems: GanttItem[],
  authorNote = '',
): Promise<ReviewSnapshot> {
  const existing = loadReviewSnapshots();
  const last = existing[existing.length - 1];
  const snapshot = await buildReviewSnapshotRecord(
    projects,
    ganttItems,
    authorNote,
    last?.contentHash ?? null,
  );
  saveReviewSnapshots([...existing, snapshot]);
  return snapshot;
}

/** Цепочка previous + пересчёт contentHash (обнаружение правок в localStorage). */
export async function verifySnapshotIntegrity(
  snapshots: ReviewSnapshot[],
): Promise<{ ok: boolean; errorAt?: number }> {
  for (let i = 0; i < snapshots.length; i++) {
    const s = snapshots[i];
    const expectedPrev = i === 0 ? null : snapshots[i - 1]!.contentHash;
    if (s.previousSnapshotHash !== expectedPrev) return { ok: false, errorAt: i };
    const recomputed = await sha256Hex(
      buildRecordForHash(s.id, s.createdAt, s.authorNote, s.payload, s.previousSnapshotHash),
    );
    if (recomputed !== s.contentHash) return { ok: false, errorAt: i };
  }
  return { ok: true };
}

/** Экспорт полного массива снимков как JSON (для архива). */
export function serializeSnapshotsForExport(snapshots: ReviewSnapshot[]): string {
  return JSON.stringify(snapshots, null, 2);
}
